import React, { useRef, useState } from "react";
import moment from "moment";
import { inject, observer } from "mobx-react";
import { withStyles } from "@material-ui/core/styles";
import distinctColors from "distinct-colors";
import _ from "lodash";

import {
  Box,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  Switch,
  Typography
} from "@material-ui/core";

import {
  CartesianGrid,
  Label,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

const ColoredCheckbox = color =>
  withStyles({
    root: {
      color: color.hex()
    }
  })(props => <Checkbox color="default" {...props} />);

function RoomSensorComparator(props) {
  function checkMeasurementsPulled(sensor) {
    if (sensor.measurements.length === 0) {
      sensor.pullMeasurements().then(() => {
        return sensor;
      });
    }
    return sensor;
  }

  //
  // Order sensors properly
  //
  let sensor_groups = [];
  let enabled_sensor_groups = new Set();
  let disabled_sensor_groups = new Set();

  props.room.sensor_groups.forEach(sg => {
    enabled_sensor_groups.add(sg.id);
    sensor_groups.push(sg);
  });

  props.room.potential_sensor_groups.forEach(sg => {
    if (!enabled_sensor_groups.has(sg.id)) {
      disabled_sensor_groups.add(sg.id);
      sensor_groups.push(sg);
    }
  });

  sensor_groups.sort((a, b) => {
    const labelRanks = {
      Standard: -20,
      Replacement: -10,
      Experimental: 0,
      New: 10
    };
    const aNotes = a.sensors[0].notesFirstValue();
    const bNotes = b.sensors[0].notesFirstValue();
    if (aNotes === bNotes) {
      return a.human_name.localeCompare(b.human_name);
    }
    return labelRanks[aNotes] - labelRanks[bNotes];
  });

  const sensors = [].concat.apply(
    [],
    sensor_groups.map(sg => sg.sensors)
  );
  const [selectedSensor, setSelectedSensor] = useState(sensors[0]);

  const [graphedSensors, setGraphedSensors] = useState([]);
  const addGraphedSensor = s =>
    setGraphedSensors(graphedSensors.filter(_s => _s.id !== s.id));
  const removeGraphedSensor = s => setGraphedSensors(graphedSensors.concat(s));

  const [aggregateGraphedSensors, setAggregateGraphedSensors] = useState(
    sensors.filter(s => enabled_sensor_groups.has(s.sensor_groups[0].id))
  );
  const addAggregateGraphedSensor = s =>
    setAggregateGraphedSensors(
      aggregateGraphedSensors.filter(_s => _s.id !== s.id)
    );
  const removeAggregateGraphedSensor = s =>
    setAggregateGraphedSensors(aggregateGraphedSensors.concat(s));

  const [metaSensors, setMetaSensors] = useState([]);
  const addMetaSensor = s =>
    setMetaSensors(metaSensors.filter(_s => _s.id !== s.id));
  const removeMetaSensor = s => setMetaSensors(metaSensors.concat(s));

  // This is wrong; we're sharing one ref between all the ListItems. However, by using isEqualNode we can paper over it for now.
  const checkboxRef = useRef();

  //
  // Merge all the data
  //

  let mergedData = [];

  const roomReadings = props.room.orderedReadings() || [];
  roomReadings.forEach(m => {
    mergedData.push(
      new Map(
        Object.entries({
          id: m.id,
          timestamp: Number(moment(m.timestamp).valueOf()),
          [props.room.id]: Number(m.payload.ppm.toFixed(1))
        })
      )
    );
  });

  graphedSensors.forEach(s => {
    (s.orderedMeasurements() || [])
      .filter(m => m.v != null)
      .forEach(m => {
        mergedData.push(
          new Map(
            Object.entries({
              timestamp: Number(moment.unix(m.t).valueOf()),
              [s.id]: Number(m.v.toFixed(1))
            })
          )
        );
      });
  });

  aggregateGraphedSensors.forEach(s => {
    let orderedMeasurements = s.orderedMeasurements() || [];
    if (s.data_ready_timestamp !== null) {
      orderedMeasurements = orderedMeasurements.filter(m =>
        moment.unix(m.t).isAfter(moment(s.data_ready_timestamp).valueOf())
      );
    }
    if (s.data_unready_timestamp !== null) {
      orderedMeasurements = orderedMeasurements.filter(m =>
        moment.unix(m.t).isBefore(moment(s.data_unready_timestamp.valueOf()))
      );
    }
    const byDay = _.groupBy(
      orderedMeasurements.filter(m => m.v != null),
      m => moment.unix(m.t).startOf("day")
    );
    new Map(Object.entries(byDay)).forEach((v, k) => {
      const key = `aggregate:${s.id}`;
      mergedData.push(
        new Map(
          Object.entries({
            timestamp: Number(moment(k).valueOf()),
            [key]: Number(_.meanBy(v, m => Number(m.v.toFixed(1))).toFixed(1))
          })
        )
      );
    });
  });

  metaSensors.forEach(s => {
    const byDay = _.groupBy(
      (s.measurements.toJSON() || []).filter(m => m.v != null),
      m => moment.unix(m.t).startOf("day")
    );
    new Map(Object.entries(byDay)).forEach((v, k) => {
      const key = `meta:${s.id}`;
      mergedData.push(
        new Map(
          Object.entries({
            timestamp: Number(moment(k).valueOf()),
            [key]: Number(_.meanBy(v, m => Number(m.v.toFixed(2))).toFixed(1))
          })
        )
      );
    });
  });

  mergedData = mergedData.sort(
    (a, b) => a.get("timestamp") - b.get("timestamp")
  );

  //
  // Figure out the colors
  //

  const palette = distinctColors({ count: sensors.length });
  let paletteMap = new Map();
  sensors.forEach((s, i) => {
    paletteMap[s.id] = palette[i];
  });

  //
  // Figure out the axes
  //

  const xAxisMin = Number(moment(roomReadings[0].timestamp).valueOf());
  const xAxisMax = Number(
    moment(roomReadings[roomReadings.length - 1].timestamp).valueOf()
  );

  const yAxisMin = Math.min(
    -25,
    ...roomReadings.map(m => Number(m.payload.ppm.toFixed(1)))
  );
  const yAxisMax = Math.max(
    75,
    ...roomReadings.map(m => Number(m.payload.ppm.toFixed(1)))
  );
  const yAxisAdjustment = (yAxisMax - yAxisMin) * 0.2;

  //
  // Put the graph together
  //

  const graph = (
    <>
      <Grid
        container
        direction="row"
        alignItems="center"
        style={{ height: "100%", width: "100%", flexWrap: "nowrap" }}
      >
        <Grid
          item
          xs
          style={{ flexBasis: "1.75rem", flexGrow: "0", flexShrink: "0" }}
        >
          <Box>
            <Box
              style={{
                writingMode: "vertical-rl",
                transform: "rotate(180deg)",
                color: "#737F8C"
              }}
            >
              <Typography>Relative Ethylene</Typography>
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          xs
          style={{
            height: "100%",
            maxWidth: "calc(100% - 1.75rem)",
            flexBasis: "0",
            flexGrow: "1",
            flexShrink: "1"
          }}
        >
          <Box style={{ width: "100%", height: "100%" }}>
            <Grid
              container
              direction="column"
              alignItems="center"
              style={{ height: "100%", width: "100%" }}
              wrap="nowrap"
            >
              <Grid
                item
                style={{ width: "100%", flex: 1, backgroundColor: "#F8F9FC" }}
              >
                <ResponsiveContainer width={"100%"} height={292 * 1.4}>
                  <LineChart data={mergedData}>
                    <CartesianGrid
                      vertical={false}
                      horizontalPoints={[
                        25,
                        50,
                        75,
                        100,
                        125,
                        150,
                        175,
                        200,
                        225,
                        250,
                        275,
                        300,
                        325,
                        350,
                        375,
                        400
                      ]}
                    />
                    <XAxis
                      hide={true}
                      type={"number"}
                      dataKey={measurement => measurement.get("timestamp")}
                      domain={[xAxisMin, xAxisMax]}
                      allowDataOverflow={true}
                    />
                    <YAxis
                      yAxisId="left"
                      hide={true}
                      type={"number"}
                      domain={[
                        yAxisMin - yAxisAdjustment,
                        yAxisMax + yAxisAdjustment
                      ]}
                      tickCount={10}
                      allowDataOverflow={true}
                    />
                    {metaSensors.length > 0 ? (
                      <YAxis
                        yAxisId="right"
                        hide={true}
                        type={"number"}
                        domain={[-5, 50]}
                        allowDataOverflow={true}
                        orientation="right"
                      />
                    ) : (
                      <></>
                    )}
                    <Line
                      key={props.room.id}
                      type={"monotone"}
                      dataKey={measurement => measurement.get(props.room.id)}
                      isAnimationActive={false}
                      dot={false}
                      name="Aggregate Ethylene"
                      stroke={"#AFC322"}
                      strokeWidth={4}
                      connectNulls={true}
                      yAxisId="left"
                    />
                    {graphedSensors.map(s => (
                      <Line
                        key={s.id}
                        type={"monotone"}
                        dataKey={measurement => measurement.get(s.id)}
                        isAnimationActive={false}
                        dot={false}
                        name={s.human_name}
                        stroke={paletteMap[s.id].hex()}
                        strokeWidth={2}
                        connectNulls={true}
                        yAxisId="left"
                      />
                    ))}
                    {aggregateGraphedSensors.map(s => (
                      <Line
                        key={`aggregate:${s.id}`}
                        type={"monotone"}
                        dataKey={measurement =>
                          measurement.get(`aggregate:${s.id}`)
                        }
                        isAnimationActive={false}
                        dot={false}
                        name={s.human_name}
                        stroke={paletteMap[s.id].hex()}
                        strokeWidth={4}
                        connectNulls={true}
                        yAxisId="left"
                      />
                    ))}
                    {metaSensors.map(s => (
                      <Line
                        key={`meta:${s.id}`}
                        type={"monotone"}
                        dataKey={measurement => measurement.get(`meta:${s.id}`)}
                        isAnimationActive={false}
                        dot={false}
                        name={s.human_name}
                        stroke={"#000000"}
                        strokeWidth={3}
                        connectNulls={true}
                        yAxisId="right"
                      >
                        <Label value={s.human_name} position="center" />
                      </Line>
                    ))}
                    <Tooltip
                      labelFormatter={value => moment(value).format("LLLL")}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>
              <Grid item style={{ width: "100%" }}>
                <Box
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <Typography>{moment(xAxisMin).format("LL")}</Typography>
                  <Typography>{moment(xAxisMax).format("LL")}</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Grid container>
      <Grid
        item
        xs={3}
        style={{ maxHeight: "calc(40rem + 137px)", overflowY: "scroll" }}
      >
        <List>
          {sensors.map(s => {
            const sg = s.sensor_groups[0];
            const UniqueColoredCheckbox = ColoredCheckbox(paletteMap[s.id]);

            return (
              <ListItem
                key={s.id}
                button
                selected={s.id === selectedSensor.id}
                onClick={e =>
                  !e.target.isEqualNode(checkboxRef.current) &&
                  setSelectedSensor(s)
                }
                style={{ paddingTop: "0", paddingBottom: "0" }}
              >
                <Switch
                  checked={enabled_sensor_groups.has(sg.id)}
                  onChange={e => {
                    e.target.checked
                      ? props.enableSensorGroup(sg.id)
                      : props.disableSensorGroup(sg.id);
                  }}
                />
                {s.human_name} - {s.notesFirstValue()[0] || "n/a"}
                <ListItemIcon>
                  <UniqueColoredCheckbox
                    inputRef={checkboxRef}
                    checked={aggregateGraphedSensors.some(_s => _s.id === s.id)}
                    onChange={e =>
                      e.target.checked
                        ? removeAggregateGraphedSensor(
                            checkMeasurementsPulled(s)
                          )
                        : addAggregateGraphedSensor(s)
                    }
                  />
                  <UniqueColoredCheckbox
                    inputRef={checkboxRef}
                    checked={graphedSensors.some(_s => _s.id === s.id)}
                    onChange={e =>
                      e.target.checked
                        ? removeGraphedSensor(checkMeasurementsPulled(s))
                        : addGraphedSensor(s)
                    }
                  />
                </ListItemIcon>
              </ListItem>
            );
          })}
        </List>
      </Grid>
      <Grid item xs={9}>
        <Box
          style={{
            width: "100%",
            height: "calc(20rem + 137px)",
            maxHeight: "calc(20rem + 137px)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "1em"
          }}
        >
          {graph}
        </Box>
        <Box>
          <Grid
            container
            style={{
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap"
            }}
          >
            {props.room.sensors
              .filter(
                s =>
                  s.human_name.split(" ")[1] !== "(batt)" &&
                  s.human_name.split(" ")[1] !== "(stm32_temp)"
              )
              .map(s => (
                <Grid item key={s.id}>
                  <Switch
                    checked={metaSensors.some(_s => _s.id === s.id)}
                    onChange={e =>
                      e.target.checked
                        ? removeMetaSensor(checkMeasurementsPulled(s))
                        : addMetaSensor(s)
                    }
                    size="small"
                  />
                  {s.human_name}
                </Grid>
              ))}
          </Grid>
        </Box>
        <br />
        <Box
          style={{
            height: "20rem",
            maxHeight: "20rem",
            overflowY: "scroll"
          }}
        >
          {selectedSensor !== undefined &&
            props.sensorElements[selectedSensor.id]}
        </Box>
      </Grid>
    </Grid>
  );
}

export default inject(stores => ({
  rooms: stores.rootStore.roomStore.rooms
}))(observer(RoomSensorComparator));
